import DateRangePicker from "components/common/DateRangePicker";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import Spinners from "components/common/Spinners";
import {
  formatDate,
  getAgeFromDOB,
  getMinMaxDate,
} from "components/common/Utils";
import {
  genderOptions,
  userRoleType,
} from "components/constants/DropdownOptions";
import { ArrowDown, ArrowUp } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import RevenueChart from "./RevenueChart";
import CustomerRequests from "./CustomerRequests";
import CurrentRewardLevel from "./CurrentRewardLevel";
import Wallet from "./Wallet";
import WalletRecharge from "./WalletRecharge";
import BookingHistory from "./BookingHistory";
import {
  getCustomerChartDetails,
  getCustomerDetails,
} from "store/actions/customerSlice";
import { useSearchParams } from "react-router-dom";
import IsLoading from "components/common/IsLoading";
import Riyal from "assets/images/icons/riyal.svg";

const CustomerDetails = () => {
  const { t } = useTranslation();
  document.title = t("Customer Details | Work Space");
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { loading, customerDetails, customerChartDetails, loading3 } =
    useSelector((state) => state.Customers);

  const [dates, setDates] = useState([]);

  useEffect(() => {
    dispatch(getCustomerDetails({ id }));
    getChartData();
  }, []);

  const getChartData = (action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const isReset = actionType === "RESET";

    const payload = {
      id,
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getCustomerChartDetails(payload));
  };

  const handleReset = () => {
    setDates([]);
    getChartData({ actionType: "RESET" });
  };

  return (
    <React.Fragment>
      <div className="page-content common_details_page order_details customer_details">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle title={t("Customer Information")} />
            </Row>
            <div className="common_wrapper">
              <Row className="top_row">
                <Col lg={12}>
                  <Row className="revenue_row">
                    <BookingCard
                      title="Workspace"
                      data={customerDetails?.bookingBreakdown?.workSpace}
                      t={t}
                    />
                    <BookingCard
                      title="Meeting Rooms"
                      data={customerDetails?.bookingBreakdown?.meetingRoom}
                      t={t}
                    />
                    <BookingCard
                      title="Workshops"
                      data={customerDetails?.bookingBreakdown?.workShop}
                      t={t}
                    />
                  </Row>
                </Col>
                <Col lg={6}>
                  <div className="common_card_inner order_customer_details">
                    <p className="fs_14 text_gray">{t("Name")}</p>
                    <h2 className="fs_20 text_black mb-2">
                      {customerDetails?.name}
                    </h2>
                    <div className="d-flex flex-wrap gap-5 mt-4">
                      <div className="inner_details">
                        <h2>{t("Gender")}</h2>
                        <h3>
                          {t(
                            genderOptions?.find(
                              (option) =>
                                option.value === customerDetails?.gender
                            )?.name
                          )}
                        </h3>
                      </div>
                      <div className="inner_details">
                        <h2>{t("Age")}</h2>
                        <h3>
                          {getAgeFromDOB(customerDetails?.dob)},{" "}
                          <span className="text_gray">
                            {customerDetails?.dob}
                          </span>
                        </h3>
                      </div>
                      <div className="inner_details">
                        <h2>{t("Status")}</h2>
                        <h3>
                          {t(
                            userRoleType?.find(
                              (option) =>
                                option.value === customerDetails?.roleType
                            )?.name
                          )}
                        </h3>
                      </div>
                      <div className="inner_details">
                        <h2>{t("Number")}</h2>
                        <h3>{customerDetails?.phone}</h3>
                      </div>
                    </div>
                    <div className="inner_details mt-4">
                      <h2>
                        {t("Member since")}{" "}
                        {formatDate(customerDetails?.createdAt)}
                      </h2>
                    </div>
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="common_card_inner price_details">
                    <h2 className="fs_20 text_black mb-2">
                      {t("Customer Device Information")}
                    </h2>
                    <div className="g_between mt-2">
                      <h2 className="fs_14">{t("Model")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.deviceInfo?.model || "-"}
                      </h2>
                    </div>
                    <div className="g_between mt-2">
                      <h2 className="fs_14">{t("Platform")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.deviceInfo?.platform || "-"}
                      </h2>
                    </div>
                    <div className="g_between mt-2">
                      <h2 className="fs_14">{t("Device Id")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.deviceInfo?.deviceId || "-"}
                      </h2>
                    </div>
                    <div className="g_between mt-2">
                      <h2 className="fs_14">{t("App Version")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.deviceInfo?.appVersion || "-"}
                      </h2>
                    </div>
                    <div className="g_between mt-2">
                      <h2 className="fs_14">{t("OS Version	")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.deviceInfo?.osVersion || "-"}
                      </h2>
                    </div>
                    <div className="g_between mt-2">
                      <h2 className="fs_14">{t("Language")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.deviceInfo?.language || "-"}
                      </h2>
                    </div>
                    {/* <div className="g_between mt-2 gap-3">
                      <h2 className="fs_14 w_fit_content">{t("FCM Token")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.userInfo?.deviceInfo?.fcmToken || "-"}
                      </h2>
                    </div> */}
                  </div>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col lg={12}>
                  <div className="common_card_inner">
                    <div>
                      <div className="g_between">
                        <h2 className="fs_20">{t("Total Revenue")}</h2>
                        <div className="d-flex gap-3 common_filter">
                          <div className="buttons">
                            <button
                              className="search_btn"
                              onClick={() =>
                                getChartData({ actionType: "SEARCH" })
                              }
                            >
                              {t("Search")}
                            </button>
                            <button className="reset_btn" onClick={handleReset}>
                              {t("Reset")}
                            </button>
                          </div>
                          <DateRangePicker dates={dates} setDates={setDates} />
                        </div>
                      </div>
                      {loading3 ? (
                        <IsLoading />
                      ) : (
                        <>
                          <h3 className="fs_40">
                            <img
                              src={Riyal}
                              width={25}
                              height={25}
                              alt="riyal"
                              className="me-1"
                            />
                            {customerChartDetails?.chartData?.totalRevenue}
                          </h3>
                          <div className="d-flex align-items-center">
                            <div
                              className={`revenue_div ${
                                ["positive", "neutral"]?.includes(
                                  customerChartDetails?.todayVsYesterdayChangeType
                                )
                                  ? "positive"
                                  : "negative"
                              }`}
                            >
                              <span className="fs_14 text_primary">
                                {["positive", "neutral"]?.includes(
                                  customerChartDetails?.todayVsYesterdayChangeType
                                ) ? (
                                  <ArrowUp className="text_primary" size={18} />
                                ) : (
                                  <ArrowDown
                                    className="text_danger"
                                    size={18}
                                  />
                                )}
                                {customerChartDetails?.todayVsYesterdayChange}
                              </span>
                            </div>
                            <span className="fs_14 mx-2">
                              {t("Number of bookings")}{" "}
                              {customerChartDetails?.chartData?.totalBookings}
                            </span>
                          </div>
                          <RevenueChart
                            categories={
                              customerChartDetails?.chartData?.categories
                            }
                            data={customerChartDetails?.chartData?.data}
                            averageRevenue={
                              customerChartDetails?.chartData?.averageRevenue
                            }
                          />
                        </>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col lg={4}>
                  <CustomerRequests
                    id={id}
                    customerReq={customerDetails?.workshopRequests}
                  />
                </Col>
                <Col lg={8}>
                  <CurrentRewardLevel
                    id={id}
                    cashbackLevels={customerDetails?.cashbackLevels}
                    walletInfo={customerDetails?.walletInfo}
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col lg={8}>
                  <Wallet id={id} />
                </Col>
                <Col lg={4}>
                  <WalletRecharge id={id} />{" "}
                </Col>
              </Row>
            </div>
            <Row className="mt-5">
              <DetailsPageTitle
                title={t("Booking History")}
                isBackBtn={false}
              />
              <div className="common_wrapper">
                <BookingHistory id={id} />
              </div>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default CustomerDetails;

const BookingCard = ({ title, data, t }) => (
  <Col lg={4}>
    <div className="common_card_inner">
      <h2 className="fs_12 text_gray">{t("Total Revenue")}</h2>
      <h2 className="fs_40 d-flex align-items-center">
        <img src={Riyal} width={30} height={30} alt="riyal" className="me-1" />
        {data?.totalRevenue}
      </h2>
      <h2 className="fs_20">{t(title)}</h2>
      <p className="fs_14 text_gray mt-1">
        {t("Total Bookings")}:{" "}
        <span className="text_primary">{data?.totalBookings}</span>
      </p>
    </div>
  </Col>
);
